import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import TypographyOverride from "../components/TypographyOverride";

export default function CaseStudies() {
  const [works, setWorks] = useState([]);

  useEffect(() => {
    api.get("/works").then(({ data }) => setWorks(data));
  }, []);

  const featured = useMemo(
    () => (works || []).filter((w) => w.case_study_enabled),
    [works]
  );

  return (
    <main className="case-studies-page pt-32 md:pt-40 pb-24 min-h-[80vh]">
      <TypographyOverride pageId="case-studies" scopeSelector=".case-studies-page" prefix="csl" />
      <section className="px-6 md:px-12 lg:px-20 mb-16 md:mb-24">
        <Reveal>
          <p className="overline mb-6">Case studies · {featured.length}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 data-testid="case-studies-page-title" className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
            The full <span className="font-serif-accent text-[#C9A050]">stories</span>.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 md:mt-10 text-[#A1A1AA] max-w-xl text-base md:text-lg font-light leading-relaxed">
            A closer look at how each project came together — the people, the problem, the idea, the execution.
          </p>
        </Reveal>
      </section>

      {featured.length === 0 && (
        <div className="px-6 md:px-12 text-center py-32">
          <p className="overline text-[#8A8A93]">
            No detailed case studies published yet
          </p>
        </div>
      )}

      <section
        data-testid="case-studies-grid"
        className="px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-16 md:gap-y-24"
      >
        {featured.map((w, idx) => {
          const vertical = w.orientation === "vertical";
          const span = vertical
            ? idx % 2 === 0
              ? "md:col-span-5"
              : "md:col-span-5 md:col-start-8 md:mt-24"
            : idx % 2 === 0
              ? "md:col-span-7"
              : "md:col-span-7 md:col-start-6 md:mt-16";
          const frame = vertical ? "aspect-[9/16]" : "aspect-[4/3]";
          return (
            <Reveal key={w.id} delay={(idx % 3) * 0.08} className={span}>
              <Link
                to={`/work/${w.slug}/case-study`}
                data-testid={`case-study-${w.slug}`}
                data-cursor="Read"
                className="group block w-full text-left"
              >
                <div className={`relative overflow-hidden ${frame} bg-[#121214]`}>
                  <picture>
                    {w.cover_image_mobile && (
                      <source media="(max-width: 767px)" srcSet={w.cover_image_mobile} />
                    )}
                    <img
                      src={w.cover_image}
                      alt={w.title}
                      loading="lazy"
                      decoding="async"
                      width={w.orientation === "vertical" ? 1080 : 1600}
                      height={w.orientation === "vertical" ? 1920 : 900}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6 md:p-8">
                    <span className="overline text-[#C9A050]">Read case study →</span>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="overline mb-3">
                    {w.category} <span className="mx-2 opacity-40">/</span> {w.year}
                  </p>
                  <h3 data-testid="cs-card-title" className="font-display text-3xl md:text-4xl tracking-tight">
                    {w.title}<span className="text-[#C9A050]">.</span>
                  </h3>
                  {w.subtitle && (
                    <p data-testid="cs-card-subtitle" className="text-[#A1A1AA] text-sm md:text-base mt-3 max-w-md leading-relaxed">
                      {w.subtitle}
                    </p>
                  )}
                </div>
              </Link>
            </Reveal>
          );
        })}
      </section>
    </main>
  );
}
